import { calculateArea, totalDistance } from "@nova/calculate-geospatial"
import { formatArea, formatDistance } from "@nova/format"
import { useAppSelector } from "integrations/redux/hooks"
import * as Icons from "@nova/component-icons"
import {
    AreaUnit,
    DistanceUnit,
    FirePerimeterMethod,
    LayerIndex,
    SidePanelLayerType,
} from "@nova/constants"
import { Perimeter } from "@nova/protobuf"
import {
    selectWorkspaceAreaUnit,
    selectWorkspaceDistanceUnit,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import {
    selectAllFirePerimeterBoundingBoxes,
    selectFirePerimeterById,
    selectFirePerimeters,
} from "features/entity-caching/fire-perimeters/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { getInspectorPanelFirePerimeter } from "features/map/inspector-panel/fire-perimeters/InspectorPanelFirePerimeter"
import {
    duplicatePerimeter,
    zoomToFirePerimeter,
} from "features/map/inspector-panel/fire-perimeters/module"
import { getGenericDuplicatableFooterActions } from "features/map/inspector-panel/inspector-panels/footers/GenericInspectorPanelFooter"
import { deletePerimeter, updatePerimeter } from "features/pubsub/entityActions/cubes/perimeter"
import { PluginRegistry } from "plugins/plugin-registry"
import GeometryLayer from "plugins/shared/layers/GeometryLayer.class"
import { useEffect, useMemo, useState } from "react"
import { Marker } from "react-map-gl"
import {
    selectEditingData,
    selectPreviewFirePerimeterPolygons,
    selectUpdateMethod,
} from "./FirePerimeterSlice"
import { FirePerimeterPluginActionBody } from "./plugin-actions/FirePerimeterPluginActionBody"
import { getGenericActiveFeatureSidePanelComponent } from "panels/content/side-panel/active-feature-components/GenericActiveFeatureSidePanelComponent"
import { useNormalPerimeterLayer, useEditingPerimeterLayer } from "./layers/usePerimeterLayer"
import { geometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { getGenericAssetsListItem } from "panels/content/side-panel/generics/genericAssetsListItems"
import { FirePerimeterAssetsThumbnail } from "panels/content/side-panel/assets-thumbnails/FirePerimeterAssetsThumbnail"
import { resolveEventLngLat } from "plugins/resolveLngLat"
import { LngLatCoordinatePair } from "features/map/event/types"
import { usePerimeterBuffersLayer } from "./layers/buffers"

export function registerPerimeterPlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "perimeter",
            displayName: "Fire Perimeter",
            sidePanelLayerType: SidePanelLayerType.FirePerimeter,
            PrimaryIcon: Icons.PerimeterPlugin,
            ImageIconUrl: "/images/plugins/layers/fire-perimeter.svg",
            updateAction: updatePerimeter,
            deleteAction: deletePerimeter,
            confirmDelete: () => true,
            protobufMessage: Perimeter,
            selectAllEntities: selectFirePerimeters,
            selectSingleEntity: selectFirePerimeterById,
            zoomTo: zoomToFirePerimeter,
            duplicate: duplicatePerimeter,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin")
        .implement("side-panel-assets-plugin", (spec) => ({
            SidePanelListItem: getGenericAssetsListItem({
                spec,
                ThumbnailRender: FirePerimeterAssetsThumbnail,
            }),
        }))
        .implement("side-panel-active-feature-plugin", (spec) => ({
            SidePanelComponent: getGenericActiveFeatureSidePanelComponent({
                spec,
                RenderComponent: FirePerimeterPluginActionBody,
            }),
        }))
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getInspectorPanelFirePerimeter(spec),
            useFooterActions: getGenericDuplicatableFooterActions(spec),
            useSubtitles,
        }))
        .implement("bounding-box-data-provider", () => ({
            useBoundingBoxData: () => useAppSelector(selectAllFirePerimeterBoundingBoxes),
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useNormalPerimeterLayer,
            zIndex: LayerIndex.FirePerimeter,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: () => {
                const projectId = useAppSelector(selectActivatedFireId)
                const { isEditor } = useWorkspacePermissions(projectId)
                const layer = useEditingPerimeterLayer()
                if (!isEditor) return []
                else return layer
            },
            zIndex: LayerIndex.GeometryBeingEdited,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: () => {
                const previewPerimeterPolygons = useAppSelector(selectPreviewFirePerimeterPolygons)
                const deckglPolygons = useMemo(() => {
                    return previewPerimeterPolygons.map((e, i) => ({ ...e, idx: i, deckGlIdx: i }))
                }, [previewPerimeterPolygons])
                return new GeometryLayer({
                    id: "fire-perimeter-preview",
                    data: deckglPolygons,
                    visible: true,
                    selectedId: undefined,
                    extraLayers: [],
                    styles: {
                        points: {},
                        selectedPoint: {},
                        midpoints: {},
                        lines: {
                            widthScale: 1,
                            widthMaxPixels: 8,
                            widthMinPixels: 2,
                            capRounded: false,
                            billboard: false,
                            getColor: [39, 187, 255, 255], // blue-500
                            getWidth: 2,
                            miterLimit: 0,
                        },
                        polygons: {
                            stroked: true,
                            getLineWidth: 3,
                            lineWidthUnits: "pixels",
                            lineMiterLimit: 0,
                            getLineColor: [39, 187, 255, 255], // blue-500
                            getFillColor: [0, 0, 0, 0],
                        },
                    },
                })
            },
            zIndex: LayerIndex.FirePerimeterPreview,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: usePerimeterBuffersLayer,
            zIndex: LayerIndex.FirePerimeterBuffers,
        }))
        .implement("map-modal-plugin", () => ({
            Modal: () => {
                const editingData = useAppSelector(selectEditingData())
                const updateMethod = useAppSelector(selectUpdateMethod())
                const points = geometryDataStore.useGeometryData("points")
                const [coords, setCoords] = useState<LngLatCoordinatePair | undefined>()
                useEffect(() => {
                    const map = document.getElementById("map")
                    const listener = (event: MouseEvent) => {
                        if (
                            points.length ||
                            !editingData ||
                            updateMethod !== FirePerimeterMethod.Manual
                        )
                            return
                        if (
                            (event.target as HTMLElement)?.className === "maplibregl-canvas" &&
                            !event.buttons
                        ) {
                            const coordinate = resolveEventLngLat(event)
                            setCoords([
                                coordinate.longitude,
                                coordinate.latitude,
                            ] as LngLatCoordinatePair)
                        } else {
                            setCoords(undefined)
                        }
                    }
                    map?.addEventListener("mousemove", listener)
                    return () => {
                        map?.removeEventListener("mousemove", listener)
                    }
                }, [editingData, points, updateMethod])

                return (
                    <>
                        {editingData &&
                        updateMethod === FirePerimeterMethod.Manual &&
                        !points.length &&
                        coords ? (
                            <Marker
                                longitude={coords[0]}
                                latitude={coords[1]}
                                anchor={"top-left"}
                                offset={[12, 16]}
                                style={{ pointerEvents: "none" }}
                            >
                                <div className="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                                    <small>Click (and drag) to start perimeter</small>
                                </div>
                            </Marker>
                        ) : null}
                    </>
                )
            },
        }))
        .implement("feature-plugin", () => ({
            sidePanelPluginActionsComponent: () => FirePerimeterPluginActionBody(),
        }))
}

function useSubtitles(id: string) {
    const perimeter = useAppSelector(selectFirePerimeterById(id))
    const organizationSettingDistance = useAppSelector(selectWorkspaceDistanceUnit)
    const organizationSettingArea = useAppSelector(selectWorkspaceAreaUnit)
    const [lineDistance, lineArea] = useMemo(() => {
        const { distance, area } = perimeter.polygons.reduce(
            (sums, cur) => {
                sums.distance += totalDistance(cur.coordinates, true)
                sums.area += calculateArea(cur.coordinates)

                return sums
            },
            { distance: 0, area: 0 }
        )
        const formattedDistance =
            formatDistance({
                value: distance,
                unit: organizationSettingDistance as DistanceUnit,
            }) + " perimeter"
        const formattedArea = area
            ? formatArea({ value: area, unit: organizationSettingArea as AreaUnit }) + " area"
            : undefined
        return [formattedDistance, formattedArea]
    }, [organizationSettingArea, organizationSettingDistance, perimeter.polygons])
    return [lineArea, lineDistance].filter((s): s is string => !!s)
}
